import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WCta } from '../WCta';
import { WregPhase } from '../WregPhase';
import { WregRadioGroup, type WregRadioOption } from '../WregRadioGroup';
import { WREG_WORKING_MS } from '../wregSteps';
import { SUBMIT_LAB_RESULTS } from '@/consts/events';
import type { WregScreenEvent } from './wregScreenEvents';

type WregLabsAnswer = 'yes' | 'no';

type Props = {
  initial?: boolean | null;
  onYes: () => void;
  onNo: () => void;
  onEvent?: WregScreenEvent;
};

const OPTIONS: WregRadioOption<WregLabsAnswer>[] = [
  ['yes', 'wreg.labs.yes'],
  ['no', 'wreg.labs.no'],
];

const toAnswer = (value: boolean | null | undefined): WregLabsAnswer | null =>
  typeof value === 'boolean' ? (value ? 'yes' : 'no') : null;

const WregLabs = ({ initial, onYes, onNo, onEvent }: Props) => {
  const { t } = useTranslation();
  const titleId = useId();
  const [ans, setAns] = useState<WregLabsAnswer | null>(toAnswer(initial));
  const [submitAttempted, setSubmitAttempted] = useState(false);
  useEffect(() => {
    setAns((prev) => prev ?? toAnswer(initial));
  }, [initial]);
  const valid = Boolean(ans);
  const showError = submitAttempted && !valid;
  const validateBeforeSubmit = () => {
    if (valid) return true;
    setSubmitAttempted(true);
    requestAnimationFrame(() => {
      const firstOption = document
        .getElementById('wreg-labs-answer')
        ?.querySelector<HTMLElement>('[role="radio"]');
      firstOption?.focus();
      firstOption?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    });
    return false;
  };
  return (
    <div className="rg-screen">
      <div className="rg-body">
        <div className="rg-inner">
          <div className="rg-head-block">
            <WregPhase n={3} />
            <h1 className="rg-h1" id={titleId}>
              {t('wreg.labs.title')}
            </h1>
            <p className="rg-sub wreg-labs-sub">{t('wreg.labs.sub')}</p>
          </div>
          <WregRadioGroup
            id="wreg-labs-answer"
            value={ans}
            onSelect={setAns}
            options={OPTIONS}
            labelledBy={titleId}
            describedBy={showError ? 'wreg-labs-answer-err' : undefined}
            invalid={showError}
          />
          {showError && (
            <span className="rg-error-hint" id="wreg-labs-answer-err" role="alert">
              Select an option to continue.
            </span>
          )}
        </div>
      </div>
      <div className="rg-foot">
        <WCta
          beforeClick={validateBeforeSubmit}
          dimmed={!valid}
          working={t('wreg.labs.cta_loading')}
          minWorkingMs={WREG_WORKING_MS}
          onClick={() => onEvent?.(SUBMIT_LAB_RESULTS)}
          onDone={() => (ans === 'yes' ? onYes() : onNo())}
        >
          {t('wreg.labs.cta')}
        </WCta>
      </div>
    </div>
  );
};

export { WregLabs };
